import { AxiosRequest } from "_services/api";
import { toastErro } from "components/Toast";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FILTROS_ITENS_LOTE_VAZIOS,
  ORDENACAO_ITENS_LOTE_PADRAO,
} from "../../../constants/filtrosItensLote";
import {
  IDadosAutorizacaoManual,
  IResultadoAlteracaoPrensa,
  IResultadoFinalizacaoSenatran,
  IResultadoReprocessamento,
} from "../../../interfaces/IAcoesLote";
import { IFiltrosItensLote } from "../../../interfaces/IFiltrosItensLote";
import { IItemLote } from "../../../interfaces/IItemLote";
import { DirecaoOrdenacao, IOrdenacao } from "../../../interfaces/IOrdenacao";
import { exportarItensLoteXlsx } from "../../../utils/exportarItensLoteXlsx";
import { mensagemDeErro } from "../../../utils/mensagemDeErro";
import { montarParametrosItensLote } from "../../../utils/montarParametrosItensLote";

const LIMITE_PADRAO = 50;
const LIMITE_TODAS_AS_PAGINAS = -1;

export function useItensLote(idLote: number) {
  const msAw = useMemo(
    () => AxiosRequest.makeApi(process.env.REACT_APP_MS_AW),
    []
  );
  const idValido = Number.isInteger(idLote) && idLote > 0;
  const [filtros, setFiltros] = useState<IFiltrosItensLote>(
    FILTROS_ITENS_LOTE_VAZIOS
  );
  const [pagina, setPagina] = useState(1);
  const [limite, setLimite] = useState(LIMITE_PADRAO);
  const [ordenacao, setOrdenacao] = useState<IOrdenacao>(
    ORDENACAO_ITENS_LOTE_PADRAO
  );
  const [versao, setVersao] = useState(0);
  const [reiniciarPaginacao, setReiniciarPaginacao] = useState(false);
  const [itens, setItens] = useState<IItemLote[]>([]);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [carregando, setCarregando] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [reprocessando, setReprocessando] = useState(false);
  const [informandoManual, setInformandoManual] = useState(false);
  const [alterandoPrensa, setAlterandoPrensa] = useState(false);
  const [finalizandoSenatran, setFinalizandoSenatran] = useState(false);
  const ultimaBusca = useRef(0);

  useEffect(() => {
    if (!idValido) return;

    const busca = ++ultimaBusca.current;

    async function buscar() {
      setCarregando(true);
      try {
        const parametros = montarParametrosItensLote({
          filtros,
          ordenacao,
          pagina,
          limite,
        });
        const { data } = await msAw.get(
          `/solicitacao-placa/lote/${idLote}/itens?${parametros}`
        );
        if (busca !== ultimaBusca.current) return;
        setItens(data?.itens ?? []);
        setTotalRegistros(data?.meta?.total ?? 0);
      } catch (erro) {
        if (busca !== ultimaBusca.current) return;
        setItens([]);
        setTotalRegistros(0);
        toastErro(mensagemDeErro(erro, "Erro ao buscar as placas do lote"));
      } finally {
        if (busca === ultimaBusca.current) setCarregando(false);
      }
    }

    buscar();
  }, [msAw, idLote, idValido, filtros, pagina, limite, ordenacao, versao]);

  const voltarParaPrimeiraPagina = useCallback(() => {
    setPagina(1);
    setReiniciarPaginacao((valor) => !valor);
  }, []);

  const recarregar = useCallback(() => {
    setVersao((valor) => valor + 1);
  }, []);

  const aplicarFiltros = useCallback(
    (novosFiltros: IFiltrosItensLote) => {
      setFiltros(novosFiltros);
      recarregar();
      voltarParaPrimeiraPagina();
    },
    [recarregar, voltarParaPrimeiraPagina]
  );

  const mudarLimite = useCallback(
    (novoLimite: number) => {
      setLimite(novoLimite);
      voltarParaPrimeiraPagina();
    },
    [voltarParaPrimeiraPagina]
  );

  // O react-data-table já volta sozinho para a página 1 quando a ordenação muda.
  const ordenar = useCallback((campo: string, direcao: DirecaoOrdenacao) => {
    setOrdenacao({ campo, direcao });
    setPagina(1);
  }, []);

  const exportar = useCallback(async () => {
    if (!idValido) return;

    setExportando(true);
    try {
      const parametros = montarParametrosItensLote({
        filtros,
        ordenacao,
        pagina: 1,
        limite: LIMITE_TODAS_AS_PAGINAS,
      });
      const { data } = await msAw.get(
        `/solicitacao-placa/lote/${idLote}/itens?${parametros}`
      );
      exportarItensLoteXlsx(idLote, data?.itens ?? []);
    } catch (erro) {
      toastErro(mensagemDeErro(erro, "Erro ao exportar as placas do lote"));
    } finally {
      setExportando(false);
    }
  }, [msAw, idLote, idValido, filtros, ordenacao]);

  const reprocessar = useCallback(
    async (
      idsSolicitacao: number[]
    ): Promise<IResultadoReprocessamento | null> => {
      setReprocessando(true);
      try {
        const { data } = await msAw.post(
          `/solicitacao-placa/lote/${idLote}/reprocessar`,
          idsSolicitacao.length ? { idsSolicitacao } : {}
        );
        recarregar();
        return data;
      } catch (erro) {
        toastErro(mensagemDeErro(erro, "Erro ao reprocessar as placas"));
        return null;
      } finally {
        setReprocessando(false);
      }
    },
    [msAw, idLote, recarregar]
  );

  const informarManual = useCallback(
    async (
      idItem: number,
      { idAutorizacao, arquivo }: IDadosAutorizacaoManual
    ): Promise<boolean> => {
      setInformandoManual(true);
      try {
        const formData = new FormData();
        formData.append("arquivo", arquivo);
        formData.append("idAutorizacao", idAutorizacao);
        await msAw.post(
          `/solicitacao-placa/lote/${idLote}/itens/${idItem}/manual`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        recarregar();
        return true;
      } catch (erro: any) {
        toastErro(
          erro?.response?.status === 413
            ? "Arquivo maior que 10 MB"
            : mensagemDeErro(erro, "Erro ao informar a autorização manual")
        );
        return false;
      } finally {
        setInformandoManual(false);
      }
    },
    [msAw, idLote, recarregar]
  );

  const alterarPrensa = useCallback(
    async (
      idsSolicitacao: number[],
      prensa: number
    ): Promise<IResultadoAlteracaoPrensa | null> => {
      setAlterandoPrensa(true);
      try {
        const { data } = await msAw.post(
          `/solicitacao-placa/lote/${idLote}/prensa`,
          { idsSolicitacao, prensa }
        );
        recarregar();
        return data;
      } catch (erro) {
        toastErro(mensagemDeErro(erro, "Erro ao alterar a prensa"));
        return null;
      } finally {
        setAlterandoPrensa(false);
      }
    },
    [msAw, idLote, recarregar]
  );

  const finalizarSenatran = useCallback(
    async (
      idsSolicitacao: number[]
    ): Promise<IResultadoFinalizacaoSenatran | null> => {
      setFinalizandoSenatran(true);
      try {
        const { data } = await msAw.post(
          `/solicitacao-placa/lote/${idLote}/senatran`,
          { idsSolicitacao }
        );
        recarregar();
        return data;
      } catch (erro) {
        toastErro(mensagemDeErro(erro, "Erro ao finalizar no Senatran"));
        return null;
      } finally {
        setFinalizandoSenatran(false);
      }
    },
    [msAw, idLote, recarregar]
  );

  return {
    itens,
    totalRegistros,
    carregando,
    limite,
    reiniciarPaginacao,
    aplicarFiltros,
    setPagina,
    mudarLimite,
    ordenar,
    exportar,
    exportando,
    reprocessar,
    reprocessando,
    informarManual,
    informandoManual,
    alterarPrensa,
    alterandoPrensa,
    finalizarSenatran,
    finalizandoSenatran,
  };
}
